// ============================================================
// CaseHero.tsx
// The case page HERO (Stage 1 shell — approved mockup "CA132B" hero), which
// replaces ViewingPaneHeader. Every behaviour of the old header is carried
// over UNCHANGED, only re-laid-out:
//   • name = the shared caseName() over the LIVE editable snapshot (updates
//     as the user types), ref = getCaseReference (shared caseRef)
//   • status Draft / Open / Closed (setupFinalised / closeDate) + the
//     all-sections-complete chip
//   • FavouriteStar, SaveIndicator, "N fields incomplete", "Also viewing"
//     presence (useCaseViewers)
//   • the assignee picker — SAME optimistic apply/revert + permission mirror
//     (admin: any active user; non-admin: Unassigned/self only when the case
//     is unassigned or theirs; otherwise read-only) — now driving both the
//     "Assign" button and the Fee-earner block
//   • Export (ExportCaseDialog — PDF options + CSV; same caseFlat/witnesses/deadlines/firm)
// NEW: the parties line, the handling team, four key facts (accident,
// limitation [deadline or est.], injuries, next deadline) and ⋯ → the shared
// CaseActionsMenu (rename/link/duplicate/status/assign/delete…).
// ============================================================
import React, { useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { getCaseReference } from '../../utils/caseHelpers'
import SaveIndicator from '../shared/SaveIndicator'
import FavouriteStar from '../shared/FavouriteStar'
import UserAvatar from '../shared/UserAvatar'
import { useCaseViewers } from '../../hooks/usePresence'
import { countErrors } from '../../utils/validation'
import { updateCaseAssigneeApi } from '../../api/casevoApi'
import ExportCaseDialog from '../shared/ExportCaseDialog'
import CaseActionsMenu, { type CaseActionsMenuState } from '../shared/CaseActionsMenu'
import type { CaseSummary } from '../layout/CaseListScreen'
import { flattenCase } from '../../hooks/useAutoSave'
import { caseExportFlat } from './caseExportFlat'
import { caseName, caseNameWithRef } from '../../utils/caseName'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { CIcon, fmtDay, todayYmd, daysBetween, relDays, yearsMonthsLeft, ymdOf } from './caseUi'
import LaterThanEstimateDot from './LaterThanEstimateDot'
import { LIMITATION_PASSED, noEstimateHint } from '../../utils/limitationRules'
import { limitationLaterCheckOf, limitationOf, limitationPassedOf, noTimeLimitOf, openDeadlinesOf, injuryAreasOf, injurySummary, accidentDescriptionOf, accidentPlaceOf } from './caseFacts'
import { estimateBadge, estimateBasisText } from '../../utils/limitationRules'
import { textDateToIso } from '../../utils/civilDate'
import { claimTypeLabel } from '../../utils/claimTypes'
import { requestFieldFocus } from '../../utils/fieldFocusRequest'
import { navigateTo } from '../../navigation/navigate'
import { CLAIM_TYPE_DATA_FIELD } from '../sections/ClaimCard'

interface CaseHeroProps {
  // All visible sequential sections complete (ViewingPane — single source).
  allSectionsComplete: boolean
  // Leave the case (after it was deleted from the ⋯ menu) — ViewingPane.goBack.
  onCaseDeleted: () => void
  // Re-read the open case after a ⋯-menu mutation (status / link / …).
  onCaseMutated: () => void
  // "N fields incomplete": the required-fields row's own count (ViewingPane,
  // requiredFieldsFor) — so the number always equals the chips.
  incompleteCount?: number
}

// ── exact mockup classes ──
const HBTN =
  'inline-flex items-center gap-[7px] text-[13px] font-semibold px-[13px] py-[8px] rounded-[9px] ' +
  'bg-white/[0.12] border border-white/[0.22] text-white hover:bg-white/20 transition-colors focus:outline-none casevo-focus'
const HBTN_IC = '!px-[9px] !rounded-[9px]'
const PILL = 'inline-flex items-center gap-[6px] text-[11px] font-semibold uppercase tracking-[0.04em] px-[10px] py-[4px] rounded-[6px]'
const K_LBL = 'text-[10.5px] tracking-[0.14em] uppercase text-white/60 font-semibold'
const FACT = 'bg-white/10 border border-white/[0.16] rounded-[12px] px-[14px] py-[11px] min-w-0'
const FACT_K = 'text-[10.5px] tracking-[0.14em] uppercase text-white/65 font-semibold flex items-center gap-[6px]'
const FACT_V = 'text-[16px] font-bold mt-[3px] flex items-center gap-[8px] whitespace-nowrap min-w-0'
const FACT_S = 'text-[12px] text-white/75 mt-[1px] truncate'
const STATUS_DOT: Record<'Draft' | 'Open' | 'Closed', string> = { Draft: 'bg-[#d6a84a]', Open: 'bg-[#40916c]', Closed: 'bg-[#9ca3af]' }

const CaseHero: React.FC<CaseHeroProps> = ({ allSectionsComplete, onCaseDeleted, onCaseMutated, incompleteCount }) => {
  const { currentCase } = useCasevoStore()
  const authUser = useCasevoStore((s) => s.authUser)
  // Real-time presence — other users currently viewing THIS case.
  const caseViewers = useCaseViewers()
  // ── ASSIGNEE PICKER STATE (moved verbatim from ViewingPaneHeader) ──
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const liveName = useLiveUserName()
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  const [savingAssignee, setSavingAssignee] = useState(false)
  const [assigneeError, setAssigneeError] = useState<string | null>(null)
  // ── PDF EXPORT ──
  const witnessesMap = useCasevoStore((s) => s.witnesses)
  const deadlinesMap = useCasevoStore((s) => s.caseDeadlines)
  const [exportOpen, setExportOpen] = useState(false)
  // ── ⋯ case actions (shared controller) ──
  const [menu, setMenu] = useState<CaseActionsMenuState | null>(null)

  if (!currentCase) return null

  const isAdmin = authUser?.role === 'admin'
  const currentUserId = authUser?.id ?? ''
  const currentAssigneeId = currentCase.assignedToUserId
  const assigneeName = currentCase.assignedTo
    ? liveName(currentCase.assignedTo.id, `${currentCase.assignedTo.firstName} ${currentCase.assignedTo.lastName}`.trim())
    : 'Unassigned'
  const assigneeFirst = assigneeName.split(' ')[0] ?? ''
  const assigneeLast = assigneeName.split(' ').slice(1).join(' ')
  // Non-admin permission mirror (matches the backend updateCaseAssignee guard).
  const nonAdminCanEdit =
    !isAdmin && (currentAssigneeId === null || currentAssigneeId === currentUserId)
  const nonAdminReadOnly =
    !isAdmin && !nonAdminCanEdit
  const loadFirmUsersIfNeeded = (): void => {
    if (!isAdmin) return
    void loadFirmUsers()
  }
  const applyAssignee = (
    display: {
      id: string
      firstName: string
      lastName: string
      avatarColour?: string
      avatarStyle?: 'solid' | 'soft' | 'icon'
      avatarPhotoUrl?: string | null
    } | null,
  ): void => {
    useCasevoStore.setState((state) => {
      if (!state.currentCase) return state
      return {
        currentCase: {
          ...state.currentCase,
          assignedToUserId: display?.id ?? null,
          assignedTo: display,
        },
      }
    })
  }
  const handleAssigneeChange = async (newId: string | null): Promise<void> => {
    if (!currentCase) return
    const previous = currentCase.assignedTo ?? null
    const previousId = currentCase.assignedToUserId
    if (newId === previousId) return
    let nextDisplay: {
      id: string
      firstName: string
      lastName: string
      avatarColour?: string
      avatarStyle?: 'solid' | 'soft' | 'icon'
      avatarPhotoUrl?: string | null
    } | null = null
    if (newId !== null) {
      const fromCache = firmUsers.find((u) => u.id === newId)
      if (fromCache) {
        nextDisplay = {
          id: fromCache.id,
          firstName: fromCache.firstName,
          lastName: fromCache.lastName,
          avatarColour: fromCache.avatarColour,
          avatarStyle: fromCache.avatarStyle,
          avatarPhotoUrl: fromCache.avatarPhotoUrl,
        }
      } else if (authUser && newId === authUser.id) {
        nextDisplay = {
          id: authUser.id,
          firstName: authUser.firstName,
          lastName: authUser.lastName,
          avatarColour: authUser.avatarColour,
          avatarStyle: authUser.avatarStyle,
          avatarPhotoUrl: authUser.avatarPhotoUrl,
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn('[CaseHero] Unknown assignee id, skipping optimistic display')
      }
    }
    applyAssignee(nextDisplay)
    setSavingAssignee(true)
    setAssigneeError(null)
    try {
      await updateCaseAssigneeApi(currentCase.id, newId)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[CaseHero] Failed to update assignee:', err)
      applyAssignee(previous)
      setAssigneeError("Couldn't update assignee")
    } finally {
      setSavingAssignee(false)
    }
  }
  // The picker <select> (same options as before). Rendered transparently
  // over the "Assign" button and the Fee-earner block — the native picker
  // opens on click, the visible chrome is the mockup's.
  const assignSelect = (label: string): React.ReactNode => (
    <select
      value={currentAssigneeId ?? ''}
      onFocus={loadFirmUsersIfNeeded}
      onChange={(e) => {
        void handleAssigneeChange(e.target.value === '' ? null : e.target.value)
      }}
      disabled={savingAssignee}
      aria-label={label}
      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-wait"
    >
      <option value="">Unassigned</option>
      {isAdmin ? (
        <>
          {currentAssigneeId &&
            !firmUsers.some((u) => u.id === currentAssigneeId) &&
            currentCase.assignedTo && (
              <option value={currentAssigneeId}>
                {assigneeName}
                {currentAssigneeId === currentUserId ? ' (you)' : ''}
              </option>
            )}
          {firmUsers
            .filter((u) => u.isActive)
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
                {u.id === currentUserId ? ' (you)' : ''}
              </option>
            ))}
        </>
      ) : (
        authUser && (
          <option value={authUser.id}>
            {authUser.firstName} {authUser.lastName} (you)
          </option>
        )
      )}
    </select>
  )

  // ── identity (live editable snapshot — same sources as before) ──
  const forename = currentCase.clientDetails.forename
  const surname = currentCase.clientDetails.surname
  const clientName = `${forename} ${surname}`.trim() || 'New Case'
  const nameSource = { id: currentCase.id, createdAt: currentCase.createdAt, caseName: currentCase.caseName, clientForename: forename, clientSurname: surname, client: currentCase.client, defendantName: currentCase.defendant?.name }
  const displayName = caseName(nameSource)
  const caseRef = getCaseReference(currentCase)
  const defendantName = currentCase.defendant?.name?.trim() ?? ''
  const isSetupFinalised = currentCase.caseControl.setupFinalised
  const isClosed = !!currentCase.caseControl.closeDate
  const statusLabel: 'Draft' | 'Open' | 'Closed' = isClosed ? 'Closed' : isSetupFinalised ? 'Open' : 'Draft'
  // ★ (Audit) The count the required-fields row's chips show (ViewingPane →
  // requiredFieldsFor: incl. the limitation chip, minus locked sections); the
  // plain validation count only where no row is shown alongside.
  const errorCount = incompleteCount ?? countErrors(currentCase)

  // ── team: fee earner (assignedTo) + the handling team (assignees) ──
  const youAreFeeEarner = !!currentAssigneeId && currentAssigneeId === currentUserId
  const team = (currentCase.assignees ?? [])
    .map((a) => a.userId)
    .filter((id) => id !== currentAssigneeId)
    .map((id) => {
      const u = firmUsers.find((f) => f.id === id)
      const nm = liveName(id, u ? `${u.firstName} ${u.lastName}`.trim() : '')
      return { id, name: nm, u }
    })
    .filter((t) => t.name)

  // ── key facts ──
  const today = todayYmd()
  const deadlines = deadlinesMap[currentCase.id]
  // Shared date rule (ISO or UK D/M/YYYY) — the same reading as the estimate.
  const accidentYmd = textDateToIso(currentCase.accidentDetails.accidentDate)
  const accidentSub = [accidentDescriptionOf(currentCase), accidentPlaceOf(currentCase)].filter(Boolean).join(' · ')
  // deadlines === undefined → the satellite load hasn't landed yet: show a
  // pending state, not an estimate / "None open" that flips a moment later.
  const deadlinesLoaded = deadlines !== undefined
  const lim = deadlinesLoaded ? limitationOf(currentCase, deadlines) : null
  const limLeft = lim ? yearsMonthsLeft(today, lim.ymd) : ''
  // (B2b) Confirmed "no time limit" (child sexual abuse, s.11ZA).
  const noLimit = deadlinesLoaded && !lim ? noTimeLimitOf(currentCase) : null
  // (B2b fix) A saved deadline later than Casevo's estimate → a calm amber dot.
  const laterCheck = deadlinesLoaded && lim && !lim.estimated ? limitationLaterCheckOf(currentCase, deadlines) : null
  // (B2b fix 2) The limitation date (saved or estimated) is before today.
  const limPassed = limitationPassedOf(currentCase, deadlines, today)
  const areas = injuryAreasOf(currentCase)
  const openedYmd = ymdOf(currentCase.caseControl.openDate) ?? ymdOf(currentCase.createdAt)
  const next = openDeadlinesOf(deadlines)[0]
  const nextDays = next ? daysBetween(today, ymdOf(next.dueDate) ?? today) : 0
  const nextPill = !next ? null : nextDays < 0 ? 'Overdue' : nextDays === 0 ? 'Today' : `${nextDays} day${nextDays === 1 ? '' : 's'}`

  // ── ⋯ menu: shared CaseActionsMenu, opened INSTANTLY ──
  // The target row is built from the case ALREADY OPEN in the store (the same
  // flat columns the autosave PUTs — flattenCase — plus the identity/status/
  // assignee fields the menu reads). No network round-trip before the menu
  // shows: previously this awaited fetchCaseById, so the click felt dead for
  // the whole request. The local copy is also the freshest (it includes
  // unsaved edits). Actions that need more (CSV / PDF export) still fetch by
  // id when clicked, inside CaseActionsMenu.
  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // TOGGLE: a second click on ⋯ closes the open menu.
    if (menu) { setMenu(null); return }
    const r = e.currentTarget.getBoundingClientRect()
    const target = {
      ...flattenCase(currentCase),
      id: currentCase.id,
      createdAt: currentCase.createdAt,
      updatedAt: currentCase.updatedAt,
      client: currentCase.client,
      matterNumber: currentCase.matterNumber,
      assignedToUserId: currentCase.assignedToUserId,
      assignedTo: currentCase.assignedTo ?? null,
      completedSections: currentCase.completedSections,
      firmWide: currentCase.firmWide,
      assignees: currentCase.assignees,
    } as unknown as CaseSummary
    setMenu({ x: Math.max(8, r.right - 240), y: r.bottom + 6, target })
  }

  const others = caseViewers.filter((v) => v.userId !== authUser?.id)
  const claimLabel = claimTypeLabel(currentCase.claim?.claimType)
  // Case control, with the Claim card opened on the claim type.
  const goToClaim = async (): Promise<void> => {
    requestFieldFocus({ section: 'automatic', field: 'claimType', dataField: CLAIM_TYPE_DATA_FIELD })
    await navigateTo({ view: 'caseDetail', caseId: currentCase.id, caseSection: 'automatic' })
  }

  return (
    <div className="leading-[normal] rounded-[14px] text-white relative overflow-hidden bg-gradient-to-br from-[#1a3a2a] to-[#2d6a4f] dark:from-[#1a4230] dark:to-[#2d6a4f]">
      <div className="px-4 sm:px-[28px] pt-[18px] sm:pt-[22px] pb-[18px] sm:pb-[20px]">
        {/* ── row 1: pills · status · save · presence · actions ── */}
        <div className="flex items-center gap-[8px] flex-wrap">
          {/* B2: the case's real claim type; not set → a muted link to Case control → Claim. */}
          {claimLabel ? (
            <span className={`${PILL} bg-white/20`}><CIcon name="brief" size={13} />{claimLabel}</span>
          ) : (
            // (Audit L1) Full-white text on the hero (well over 4.5:1 — the
            // muted look comes from the dashed outline + no fill). The
            // accessible name says what it does; it still contains the
            // visible words (WCAG label-in-name).
            <button type="button" onClick={() => { void goToClaim() }} title="Set the claim type in Case control" aria-label="Set the claim type (claim type not set)"
              className={`${PILL} bg-transparent text-white !normal-case !tracking-normal border border-dashed border-white/60 hover:bg-white/15 focus:outline-none casevo-focus`}>
              <CIcon name="brief" size={13} />Claim type not set
            </button>
          )}
          <span className={`${PILL} bg-white/10 font-mono !normal-case !tracking-normal`}>{caseRef}</span>
          <span className="inline-flex items-center gap-[7px] text-[11px] font-bold uppercase tracking-[0.05em] pl-[8px] pr-[10px] py-[4px] rounded-full bg-white text-[#1f4a35]">
            <i className={`w-[7px] h-[7px] rounded-full ${STATUS_DOT[statusLabel]}`} aria-hidden="true" />{statusLabel}
          </span>
          {allSectionsComplete && (
            <span className={`${PILL} bg-white/20`}><CIcon name="check" size={12} strokeWidth={3} />Setup complete</span>
          )}
          <SaveIndicator />
          {errorCount > 0 && (
            <span className="text-[12px] text-[#ffe2a6]">{errorCount} field{errorCount === 1 ? '' : 's'} incomplete</span>
          )}
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-[8px] flex-wrap">
            {others.length > 0 && (
              <span className="flex items-center gap-[8px] text-[11px] text-white/70 mr-[4px]" title={`Also viewing: ${others.map((v) => liveName(v.userId, v.name)).join(', ')}`}>
                Also viewing
                <span className="flex">
                  {others.slice(0, 4).map((v, i) => (
                    <span key={v.userId} className={`inline-flex rounded-full ring-2 ring-[#24563f] ${i ? '-ml-[6px]' : ''}`}>
                      <UserAvatar userId={v.userId} fallbackName={v.name} fallbackLook={{ colour: v.avatarColour, style: v.avatarStyle, photoUrl: v.avatarPhotoUrl }} size="rail" title={false} />
                    </span>
                  ))}
                </span>
                {others.length > 4 && <span>+{others.length - 4}</span>}
              </span>
            )}
            <FavouriteStar
              itemType="case"
              itemId={currentCase.id}
              label={caseNameWithRef(nameSource)}
              variant="dark"
              size={15}
              filledClassName="text-casevo-gold hover:text-casevo-gold"
              idleClassName="text-white/85 hover:text-white"
              className={`${HBTN.replace(' text-white', '')} ${HBTN_IC}`}
            />
            {!nonAdminReadOnly && (
              <span className={`${HBTN} relative focus-within:ring-2 focus-within:ring-white/70`}>
                <CIcon name="user" size={15} />Assign
                {assignSelect('Assign case')}
              </span>
            )}
            {/* ★ (Limitation audit) Disabled until the deadlines have loaded — an
                early export printed the accident + 3y ESTIMATE in place of a
                saved limitation date. */}
            <button type="button" onClick={() => setExportOpen(true)} className={`${HBTN} disabled:opacity-60 disabled:cursor-not-allowed`}
              disabled={deadlinesMap[currentCase.id] === undefined}
              title={deadlinesMap[currentCase.id] === undefined ? 'Loading deadlines…' : undefined}>
              <CIcon name="dl" size={15} />Export
            </button>
            <button
              type="button"
              // CaseContextMenu closes on a WINDOW mousedown outside itself
              // (CaseContextMenu.tsx:105-113). While it's open, stop this
              // button's mousedown from reaching that listener — otherwise the
              // outside-close runs first and the click below would RE-open it.
              // The click handler then does the close (toggle). React's
              // stopPropagation halts the native event at the root, so the
              // window listener never sees it; any other spot still closes.
              onMouseDown={(e) => { if (menu) e.stopPropagation() }}
              onClick={toggleMenu}
              className={`${HBTN} ${HBTN_IC}`}
              aria-label="More case actions"
              title="More case actions"
              aria-haspopup="menu"
              aria-expanded={!!menu}
            >
              <CIcon name="dots" size={15} />
            </button>
          </div>
        </div>
        {assigneeError && <div className="mt-[6px] text-[12px] text-red-300">{assigneeError}</div>}

        {/* ── row 2: title + parties · team ── */}
        <div className="flex flex-col md:flex-row md:items-end gap-[14px] md:gap-[24px] mt-[14px]">
          <div className="min-w-0">
            <h1 className="m-0 text-[22px] sm:text-[28px] font-bold tracking-[-0.015em] leading-[1.15] break-words">{displayName}</h1>
            <div className="text-[14px] text-white/[0.88] flex items-center gap-[8px] mt-[5px] flex-wrap">
              <span>{clientName}</span>
              <span className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-white/55">v</span>
              <span className={defendantName ? '' : 'text-white/55 italic'}>{defendantName || 'Defendant not set'}</span>
              {currentCase.matterNumber != null && (
                <><span className="opacity-45" aria-hidden="true">·</span><span>Matter {currentCase.matterNumber}</span></>
              )}
            </div>
          </div>
          <div className="md:ml-auto self-start md:self-auto flex items-center gap-[12px] bg-white/[0.08] border border-white/[0.14] rounded-[12px] px-[12px] py-[9px]">
            <div className={`relative ${nonAdminReadOnly ? '' : 'rounded-[8px] focus-within:ring-2 focus-within:ring-white/70'}`}>
              <div className={K_LBL}>Fee earner</div>
              <div className="flex items-center gap-[9px] mt-[4px]">
                {currentCase.assignedTo ? (
                  <span className="inline-flex rounded-full ring-2 ring-white/25">
                    <UserAvatar
                      userId={currentCase.assignedTo.id}
                      fallbackName={`${assigneeFirst} ${assigneeLast}`.trim()}
                      fallbackLook={{ colour: currentCase.assignedTo.avatarColour, style: currentCase.assignedTo.avatarStyle, photoUrl: currentCase.assignedTo.avatarPhotoUrl }}
                      size="sm"
                      title={false}
                    />
                  </span>
                ) : (
                  <span className="w-8 h-8 rounded-full border border-dashed border-white/40" aria-hidden="true" />
                )}
                <span className="text-[14px] font-bold flex items-center gap-[6px]">
                  {assigneeName}
                  {youAreFeeEarner && <span className="text-[10px] font-bold px-[6px] py-[2px] rounded-[5px] bg-casevo-gold text-[#3a2e08]">You</span>}
                </span>
              </div>
              {!nonAdminReadOnly && assignSelect('Change fee earner')}
            </div>
            {team.length > 0 && (
              <div className="pl-[12px] border-l border-white/15">
                <div className={K_LBL}>Team</div>
                <div className="flex mt-[6px]" title={team.map((t) => t.name).join(', ')}>
                  {team.slice(0, 5).map((t, i) => (
                    <span key={t.id} className={`inline-flex rounded-full ring-2 ring-[#2a5a43] ${i ? '-ml-[6px]' : ''}`}>
                      <UserAvatar userId={t.id} fallbackName={t.name} size="rail" title={false} />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── row 3: key facts ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-[10px] mt-[18px]">
          <div className={FACT}>
            <div className={FACT_K}><CIcon name="car" size={12} />Accident</div>
            <div className={FACT_V}><span className="truncate">{accidentYmd ? fmtDay(accidentYmd) : 'Not recorded'}</span></div>
            <div className={FACT_S}>{accidentSub || (accidentYmd ? 'Accident details' : 'Add in Accident details')}</div>
          </div>
          <div className={FACT}>
            <div className={FACT_K}><CIcon name="scale" size={12} />Limitation</div>
            <div className={FACT_V}>
              <span className="truncate">{lim ? fmtDay(lim.ymd) : noLimit ? 'No time limit' : deadlinesLoaded ? 'Not set' : '…'}</span>
              {laterCheck && <LaterThanEstimateDot message={laterCheck.message} className="bg-[#f6d77e]" />}
              {lim?.estimated && (
                // The short badge here (the fact cell is narrow); "no date of
                // birth" goes on the line below instead of squeezing the date.
                <span className="shrink-0 whitespace-nowrap text-[10px] font-bold tracking-[0.06em] uppercase px-[6px] py-[1px] rounded-[4px] border border-white/35 text-white/80" title={lim.basis === 'override' ? 'Own date (Case control → Claim). Add a “Limitation date” deadline for alerts.' : `Estimated: ${estimateBasisText(lim.basis)}. Add a “Limitation date” deadline to replace it.`}>{lim.basis === 'override' ? 'own' : 'est.'}</span>
              )}
            </div>
            <div className={FACT_S} title={lim?.estimated ? `${limLeft} · ${estimateBasisText(lim.basis)}` : undefined}>
              {lim ? `${limLeft} · ${!lim.estimated ? 'Limitation date deadline' : lim.basis === 'noDob' ? estimateBadge(lim.basis) : estimateBasisText(lim.basis)}` : noLimit ? 'Confirmed · Limitation Act s.11ZA' : deadlinesLoaded ? noEstimateHint(currentCase.claim?.claimType, true) : 'Loading…'}
            </div>
            {/* (B2b fix 2) red, calm — on the dark hero, a light red */}
            {limPassed && <div role="status" className="mt-[3px] inline-flex items-center gap-1 text-[12px] font-semibold text-[#ffb4a8]"><CIcon name="flag" size={12} />{LIMITATION_PASSED}</div>}
          </div>
          <div className={FACT}>
            <div className={FACT_K}><CIcon name="heart" size={12} />Injuries</div>
            <div className={FACT_V}><span className="truncate">{areas.length ? injurySummary(areas) : 'Not recorded'}</span></div>
            <div className={FACT_S}>{openedYmd ? `${isSetupFinalised ? 'Opened' : 'Created'} ${fmtDay(openedYmd)}` : ''}</div>
          </div>
          <div className={FACT}>
            <div className={FACT_K}><CIcon name="flag" size={12} />Next deadline</div>
            <div className={FACT_V}>
              <span className="truncate">{next ? next.deadlineType.name : deadlinesLoaded ? 'None open' : '…'}</span>
              {nextPill && (
                <span className={`shrink-0 text-[10.5px] font-bold px-[7px] py-[2px] rounded-[5px] ${nextDays < 0 ? 'bg-[rgba(243,160,148,0.25)] text-[#ffd2cb]' : 'bg-[rgba(255,208,128,0.22)] text-[#ffe2a6]'}`}>{nextPill}</span>
              )}
            </div>
            <div className={FACT_S}>{next ? `Due ${fmtDay(next.dueDate)} · ${relDays(nextDays)}` : deadlinesLoaded ? 'Add one in Deadlines' : 'Loading…'}</div>
          </div>
        </div>
      </div>

      <ExportCaseDialog
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        // ★ (Audit) Includes the linked client record, so the PDF's DOB is
        // THE case DOB (case copy, else the record) — as this banner.
        caseFlat={caseExportFlat(currentCase)}
        witnesses={witnessesMap[currentCase.id] ?? []}
        deadlines={deadlinesMap[currentCase.id] ?? []}
        firmName={authUser?.firmName}
      />
      <CaseActionsMenu
        menu={menu}
        onClose={() => setMenu(null)}
        onMutated={(id, kind) => {
          if (id !== currentCase.id) return
          if (kind === 'deleted') onCaseDeleted()
          else onCaseMutated()
        }}
      />
    </div>
  )
}

export default CaseHero
